import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectLoggedInUserId } from "../../../../Store/features/user/userSlice";
import AddProductPopup from "./AddProductPopup.jsx";
import axios from "axios";
import { environment } from "../../../../environments/environment";
import { toast } from "react-toastify";
import PurchasePopup from "../../../../components/Shared/PurchasePopup/PurchasePopup";
import { load } from "@cashfreepayments/cashfree-js";
import ProductCard from "./ProductCard/ProductCard.jsx";
import { deleteProductFromCommunity } from "../../../../Service/user";
import "./Products.scss";
import AddServicePopup from "./AddServicePopup.jsx";
import ServiceTabs from "./ServiceTabs.jsx";
import PriorityDMCard from "./ServiceCards/PriorityDMCard.jsx";
import MeetingCard from "./ServiceCards/MeetingCard.jsx";
import {
	deletePriorityDMAPI,
	bookPriorityDMAPI,
	deleteServiceMeetingAPI,
	bookServiceMeetingAPI,
	createPaymentSession,
	verifyPayment,
	disableEvent,
	getReleventUserMeetings,
	getWebinarsByCommunityId,
	deleteWebinar
} from "../../../../Service/user";
import EventCard from "./ServiceCards/EventCard";
import Webinars from "../../../Meetings/Webinars/Webinars";
import WebinarCard from "./ServiceCards/WebinarCard.jsx";
import { IoChevronDownOutline } from "react-icons/io5";
import UpdateShareBanner from "../../../Shared/UpdateShareBanner/UpdateShareBanner";
import CreatePostPopUp from "../../../PopUp/CreatePostPopUp/CreatePostPopUp";

const Products = ({ community: initialCommunity }) => {
	const [showAddProduct, setShowAddProduct] = useState(false);
	const [showUrlsPopup, setShowUrlsPopup] = useState(null);
	const [products, setProducts] = useState([]);
	const [communityData, setCommunityData] = useState(null);
	const loggedInUserId = useSelector(selectLoggedInUserId);
	const isAdmin = communityData?.adminId._id === loggedInUserId;
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [updateProduct, setUpdateProduct] = useState(null);
	const [activeTab, setActiveTab] = useState("products");
	const [showAddService, setShowAddService] = useState(false);
	const [editingService, setEditingService] = useState(null);
	const [relevantMeetings, setRelevantMeetings] = useState([]);
	const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
	const [webinars, setWebinars] = useState([]);
	const [isLoadingWebinars, setIsLoadingWebinars] = useState(false);
	const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
	const [showActionDropdown, setShowActionDropdown] = useState(false);
	// Add update banner and create post popup states
	const [showUpdateBanner, setShowUpdateBanner] = useState(false);
	const [popupOpen, setPopupOpen] = useState(false);
	const [updatedEntityId, setUpdatedEntityId] = useState(null);
	const [updateType, setUpdateType] = useState("");
	// const [deletingProduct, setDeletingProduct] = useState([]);
	// console.log("product", products);

	// Add this useEffect to handle both states
	useEffect(() => {
		if (initialCommunity) {
			setProducts(initialCommunity.products || []);
			setCommunityData(initialCommunity);
		}
	}, [initialCommunity]);

	// Add useEffect to fetch relevant meetings when tab changes to meetings
	useEffect(() => {
		const fetchRelevantMeetings = async () => {
			if (activeTab === "meetings" && !isAdmin) {
				setIsLoadingMeetings(true);
				try {
					const token = localStorage.getItem("accessToken");
					const response = await getReleventUserMeetings(communityData._id, loggedInUserId);
					if (response.status === 200) {
						setRelevantMeetings(response.data);
					}
				} catch (error) {
					console.error("Error fetching relevant meetings:", error);
					toast.error("Failed to fetch meetings");
				} finally {
					setIsLoadingMeetings(false);
				}
			}
		};

		fetchRelevantMeetings();
	}, [activeTab, communityData?._id, loggedInUserId, isAdmin]);

	// Add useEffect to fetch webinars when tab changes to webinars
	useEffect(() => {
		const fetchWebinars = async () => {
			setIsLoadingWebinars(true);
			try {
				const response = await getWebinarsByCommunityId(communityData._id);
				if (response.status === 200) {
					setWebinars(response.data);
				} else {
					console.error("Error fetching webinars:", response);
				}
			} catch (error) {
				console.error("Error fetching webinars:", error);
			} finally {
				setIsLoadingWebinars(false);
			}
		};
		fetchWebinars();
	}, [activeTab, communityData?._id]);
	// Initialize Cashfree SDK
	const initializeCashfree = async () => {
		try {
			return await load({ mode: "production" });
		} catch (error) {
			console.error("Failed to initialize Cashfree:", error);
			throw error;
		}
	};

	const handleBuyProduct = async (product) => {
		if (product.is_free) {
			await processPurchase(product._id);
		} else {
			setSelectedProduct(product);
			setShowPaymentModal(true);
		}
	};

	const processPurchase = async (productId) => {
		try {
			const token = localStorage.getItem("accessToken");
			await axios.post(
				`${environment.baseUrl}/communities/buyProduct/${communityData._id}/${productId}`,
				{},
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			toast.success("Product acquired successfully!");
			// Update products list to reflect the purchase
			setProducts((prevProducts) =>
				prevProducts.map((p) =>
					p._id === productId
						? {
								...p,
								purchased_by: [...(p.purchased_by || []), loggedInUserId],
						  }
						: p
				)
			);
		} catch (error) {
			toast.error("Error acquiring product");
			console.error("Purchase error:", error);
		}
	};

	const handlePaymentSuccess = async (userDetails) => {
		try {
			const cashfree = await initializeCashfree();

			const paymentResponse = await createPaymentSession({
				name: userDetails.name,
				email: userDetails.email,
				mobile: userDetails.mobile,
				amount: selectedProduct.amount,
				entityId: selectedProduct._id,
				entityType: "product",
			});

			const { order_id, payment_session_id } = paymentResponse.data;

			await cashfree.checkout({
				paymentSessionId: payment_session_id,
				redirectTarget: "_modal",
			});

			const verificationResponse = await verifyPayment({
				orderId: order_id,
				entityId: selectedProduct._id,
				entityType: "product",
			});

			if (verificationResponse.data.status === "SUCCESS") {
				await processPurchase(selectedProduct._id);
				const updatedCommunity = await fetchCommunityDetails();
				setProducts(updatedCommunity.products || []);
			} else {
				toast.error("Payment verification failed");
			}
		} catch (error) {
			console.error("Payment error:", error);
			toast.error("Payment process failed");
		} finally {
			setShowPaymentModal(false);
		}
	};

	const handleProductAdded = (newProduct) => {
		setProducts((prevProducts) => [...prevProducts, newProduct]);
		setShowAddProduct(false);
		toast.success("Product added successfully!");
		// Show update banner after product is added
		setUpdatedEntityId(newProduct._id);
		setUpdateType("product");
		setShowUpdateBanner(true);
	};

	const fetchCommunityDetails = async () => {
		try {
			const token = localStorage.getItem("accessToken");
			const response = await axios.get(
				`${environment.baseUrl}/communities/getCommunity/${communityData._id}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			return response.data.data;
		} catch (error) {
			console.error("Error fetching community details:", error);
			toast.error("Failed to refresh community data");
			return null;
		}
	};
	const handleDeleteProduct = async (communityId, productId) => {
		try {
			// setDeletingProduct((prev) => [...prev, productId]);
			await deleteProductFromCommunity(communityId, productId);
			toast.success("Product deleted successfully!");

			// Update the products state to remove the deleted product
			setProducts((prevProducts) =>
				prevProducts.filter((product) => product._id !== productId)
			);
		} catch (error) {
			toast.error("Error deleting product");
			console.error("Delete error:", error);
		} finally {
			// setDeletingProduct((prev) => prev.filter((id) => id !== productId));
		}
	};

	const handleDeleteEvent = async (communityId, eventId) => {
		try {
			
			await disableEvent(eventId);
			toast.success("Event deleted successfully!");

			// Update the products state to remove the deleted product
			setCommunityData((prevCommunity) => ({
				...prevCommunity,
				services: {
					...prevCommunity.services,
					events: prevCommunity.services.events.filter(e => e.eventId._id !== eventId)
				}
			}));
		} catch (error) {
			toast.error("Error deleting product");
			console.error("Delete error:", error);
		} finally {
			toast.success("Event deleted successfully!");
		}
	};

	const handleUpdateProduct = (newProduct) => {
		// Update the product in the local state
		setProducts((prevProducts) =>
			prevProducts.map((product) =>
				product._id === newProduct._id ? newProduct : product
			)
		);

		setShowAddProduct(false);
		toast.success("Product updated successfully!");
		
		// Show update banner after product is updated
		setUpdatedEntityId(newProduct._id);
		setUpdateType("product-update");
		setShowUpdateBanner(true);
	};

	const handleServiceAdded = (newService) => {
		if (newService?.webinarType) {
			// Handle webinar updates/creation
			setWebinars(prev => {
				if (editingService) {
					// Update existing webinar
					return prev.map(webinar => 
						webinar._id === newService._id ? newService : webinar
					);
				} else {
					// Add new webinar
					return [...prev, newService];
				}
			});
		} else {
			// Handle other services as before
			setCommunityData(prev => {
				const services = { ...prev.services };
				
				if (editingService) {
					if (newService?.timeline) {
						services.priorityDMs = services.priorityDMs.map(dm => 
							dm._id === newService._id ? newService : dm
						);
					} else if (newService?.eventType) {
						services.events = services.events.map(e => 
							e.eventId._id === newService._id 
								? { ...e, eventId: newService }
								: e
						);
					} else {
						services.meetings = services.meetings.map(meeting => 
							meeting._id === newService._id ? newService : meeting
						);
					}
				} else {
					if (newService?.timeline) {
						services.priorityDMs = [...(services.priorityDMs || []), newService];
					} else if (newService?.eventType) {
						services.events = [...(services.events || []), { eventId: newService }];
					} else {
						services.meetings = [...(services.meetings || []), newService];
					}
				}

				return {
					...prev,
					services
				};
			});
		}

		setShowAddService(false);
		setEditingService(null);
		toast.success(`Service ${editingService ? 'updated' : 'added'} successfully!`);
		
		// Show update banner after service is added/updated
		setUpdatedEntityId(newService._id);
		setUpdateType(editingService ? "service-update" : "service");
		setShowUpdateBanner(true);
	};

	const handleDeleteDM = async (communityId, dmId) => {
		try {
			await deletePriorityDMAPI(communityId, dmId);
			// Update the community state to reflect the deletion
			setCommunityData(prev => ({
				...prev,
				services: {
					...prev.services,
					priorityDMs: prev.services.priorityDMs.filter(dm => dm._id !== dmId)
				}
			}));
			toast.success("Priority DM deleted successfully!");
		} catch (error) {
			console.error("Error deleting Priority DM:", error);
			toast.error("Failed to delete Priority DM");
		}
	};

	const handleUpdateDM = (dm) => {
		setEditingService({ ...dm, type: 'priorityDM' });
		setShowAddService(true);
	};

	const handleAskQuestion = async (communityId, dmId, question) => {
		try {
			const response = await bookPriorityDMAPI(communityId, dmId, { question });
			// Update the community state to reflect the new question
			setCommunityData(prev => ({
				...prev,
				services: {
					...prev.services,
					priorityDMs: prev.services.priorityDMs.map(dm => 
						dm._id === dmId ? {
							...dm,
							questions: [...dm.questions, response.data]
						} : dm
					)
				}
			}));
			toast.success("Question submitted successfully!");
		} catch (error) {
			console.error("Error submitting question:", error);
			toast.error("Failed to submit question");
		}
	};

	const handleDeleteMeeting = async (communityId, meetingId) => {
		try {
			await deleteServiceMeetingAPI(communityId, meetingId);
			// Update the community state to reflect the deletion
			setCommunityData(prev => ({
				...prev,
				services: {
					...prev.services,
					meetings: prev.services.meetings.filter(m => m._id !== meetingId)
				}
			}));
			toast.success("Meeting deleted successfully!");
		} catch (error) {
			console.error("Error deleting Meeting:", error);
			toast.error("Failed to delete Meeting");
		}
	};

	const handleMeetingUpdate = (meeting) => {
		setEditingService({ ...meeting, type: 'meeting' });
		setShowAddService(true);
	};

	const handleBookMeeting = async (communityId, meetingId, slot) => {
		try {
			const response = await bookServiceMeetingAPI(communityId, meetingId, { slot });
			
			// Update the community state with new name
			setCommunityData(prev => {
				const updatedCommunity = {
					...prev,
					services: {
						...prev.services,
						meetings: prev.services.meetings.map(m => {
							if (m._id === meetingId) {
								const updatedAvailability = m.availability.map(day => {
									if (new Date(day.day).toDateString() === new Date(slot.day).toDateString()) {
										return {
											...day,
											slots: day.slots.map(s => 
												s.startTime === slot.startTime ? { ...s, isBooked: true } : s
											)
										};
									}
									return day;
								});

								return {
									...m,
									availability: updatedAvailability,
									bookings: [...m.bookings, response.data]
								};
							}
							return m;
						})
					}
				};
				
				return updatedCommunity;
			});
			
			toast.success("Meeting booked successfully!");
			return response.data;
		} catch (error) {
			console.error("Error booking Meeting:", error);
			toast.error("Failed to book Meeting");
			throw error;
		}
	};

	const handleEventUpdate = async (updatedEvent) => {
		try {
			setEditingService({ ...updatedEvent, type: 'event' });
			setShowAddService(true);
		} catch (error) {
			console.error("Error updating event:", error);
			toast.error("Failed to update event");
		}
	};

	const handleDeleteWebinar = async (webinarId) => {
		try {
			await deleteWebinar(webinarId);
			toast.success("Webinar deleted successfully!");

			// Update the products state to remove the deleted webinar
			setWebinars(prev => prev.filter(w => w._id !== webinarId));
		} catch (error) {
			console.error("Error deleting webinar:", error);
			toast.error("Failed to delete webinar");
		}
	};

	const handleWebinarUpdate = async (updatedWebinar) => {
		try {
			setEditingService({ ...updatedWebinar, type: 'webinar' });
			setShowAddService(true);
		} catch (error) {
			console.error("Error updating webinar:", error);
			toast.error("Failed to update webinar");
		}
	};

	// Add useEffect to reset update state when banner closes
	useEffect(() => {
		if (!showUpdateBanner) {
			setUpdatedEntityId(null);
			setUpdateType("");
		}
	}, [showUpdateBanner]);

	const handlePreparePost = () => {
		// Store post content in sessionStorage to be picked up by CreatePostPopUp
		sessionStorage.setItem('update_summary', generatePostContent());
		sessionStorage.setItem('update_community_id', communityData._id);
		setPopupOpen(true);
	};

	const handleUpdateBannerClose = () => {
		setShowUpdateBanner(false);
	};

	const adminActions = [
		{ id: 'product', label: 'Add Product', onClick: () => setShowAddProduct(true) },
		{ id: 'service', label: 'Add Service', onClick: () => setShowAddService(true) },
	];

	// Add a function to generate initial post content based on update type
	const generatePostContent = () => {
		let content = '';
		
		if (updateType.includes('product')) {
			const isUpdate = updateType === 'product-update';
			content = isUpdate 
				? `I've just updated a product in my community! Check it out.` 
				: `I've just added a new product to my community! Check it out.`;
		} else if (updateType.includes('service')) {
			const isUpdate = updateType === 'service-update';
			content = isUpdate
				? `I've just updated a service in my community! Come see what's changed.`
				: `I've just added a new service to my community! Come check it out.`;
		} else if (updateType === 'webinar') {
			content = `I've just scheduled a new webinar in my community! Join us and learn something new.`;
		} else if (updateType === 'event') {
			content = `I've just created a new event in my community! I hope to see you there.`;
		}
		
		return content;
	};

	return (
		<div className="products-container">
			<ServiceTabs activeTab={activeTab} setActiveTab={setActiveTab} />
			
			<div className="content-section">
				{isAdmin && (
					<div className="action-buttons">
						{/* Desktop Actions */}
						<div className="desktop-actions">
							{adminActions.map(action => (
								<button key={action.id} className="add-btn" onClick={action.onClick}>
									+ {action.label}
								</button>
							))}
						</div>

						{/* Mobile Actions Dropdown */}
						<div className="mobile-actions">
							<button 
								className="action-dropdown-toggle"
								onClick={() => setShowActionDropdown(!showActionDropdown)}
							>
								<span>Add New</span>
								<IoChevronDownOutline className={`icon ${showActionDropdown ? 'open' : ''}`} />
							</button>
							{showActionDropdown && (
								<div className="action-dropdown-menu">
									{adminActions.map(action => (
										<button
											key={action.id}
											className="action-dropdown-item"
											onClick={() => {
												action.onClick();
												setShowActionDropdown(false);
											}}
										>
											{action.label}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				)}

				{activeTab === "products" && (
					<div className="products-grid">
						{products.length > 0 ? (
							products.map((product) => (
								<ProductCard
									communityId={communityData._id}
									key={product._id}
									product={product}
									onBuy={handleBuyProduct}
									onAccess={() => setShowUrlsPopup(product)}
									loggedInUserId={loggedInUserId}
									isAdmin={isAdmin}
									onDelete={() => handleDeleteProduct(communityData._id, product._id)}
									onUpdate={() => {
										setShowAddProduct(!showAddProduct);
										setUpdateProduct(product);
									}}
								/>
							))
						) : (
							<div className="no-items-message">No products available for this community</div>
						)}
					</div>
				)}

				{activeTab === "priorityDMs" && (
					<div className="services-grid">
						{communityData?.services?.priorityDMs?.length > 0 ? (
							communityData.services.priorityDMs.map((dm) => (
								<PriorityDMCard
									communityId={communityData._id}
									key={dm._id}
									dm={dm}
									isAdmin={isAdmin}
									loggedInUserId={loggedInUserId}
									onDelete={() => handleDeleteDM(communityData._id, dm._id)}
									onUpdate={handleUpdateDM}
									onAsk={(question) => handleAskQuestion(communityData._id, dm._id, question)}
								/>
							))
						) : (
							<div className="no-items-message">No priority DMs available for this community</div>
						)}
					</div>
				)}

				{activeTab === "meetings" && (
					<div className="services-grid">
						{isLoadingMeetings ? (
							// Show 3 skeleton cards while loading
							Array(3).fill(0).map((_, index) => (
								<div key={index} className="service-card meeting-card skeleton">
									<div className="service-content">
										<div className="skeleton-title"></div>
										<div className="skeleton-description"></div>
										<div className="skeleton-details">
											<div className="skeleton-duration"></div>
											<div className="skeleton-badge"></div>
										</div>
										<div className="skeleton-topics">
											<div className="skeleton-tag"></div>
											<div className="skeleton-tag"></div>
										</div>
									</div>
									<div className="skeleton-button"></div>
								</div>
							))
						) : (isAdmin ? communityData?.services?.meetings : relevantMeetings)?.length > 0 ? (
							(isAdmin ? communityData.services.meetings : relevantMeetings).map((meeting) => (
								<MeetingCard
									key={meeting._id}
									meeting={meeting}
									isAdmin={isAdmin}
									loggedInUserId={loggedInUserId}
									onDelete={() => handleDeleteMeeting(communityData._id, meeting._id)}
									onUpdate={() => handleMeetingUpdate(meeting)}
									onBook={(slot) => handleBookMeeting(communityData._id, meeting._id, slot)}
									communityId={communityData._id}
								/>
							))
						) : (
							<div className="no-items-message">No meetings available for this community</div>
						)}
					</div>
				)}

				{activeTab === "events" && (
					<div className="services-grid">
						{communityData?.services?.events?.filter(event => event.eventId.isActive)?.length > 0 ? (
							communityData.services.events
								.filter(event => event.eventId.isActive)
								.map((event) => (
									<EventCard
										key={event.eventId._id}
										event={event.eventId}
										isAdmin={isAdmin}
										username={communityData.adminId.userName}
										onDelete={(eventId) => handleDeleteEvent(communityData._id, eventId)}
										onUpdate={handleEventUpdate}
										communityId={communityData._id}
									/>
								))
						) : (
							<div className="no-items-message">No events available for this community</div>
						)}
					</div>
				)}
				{activeTab === "webinars" && (
					<div className="services-grid">
						{isLoadingWebinars ? (
							// Show 3 skeleton cards while loading
							Array(3).fill(0).map((_, index) => (
								<div key={index} className="service-card webinar-card skeleton">
									<div className="service-content">
										<div className="skeleton-title"></div>
										<div className="skeleton-description"></div>
										<div className="skeleton-details">
											<div className="skeleton-duration"></div>
											<div className="skeleton-badge"></div>
										</div>
										<div className="skeleton-topics">
											<div className="skeleton-tag"></div>
											<div className="skeleton-tag"></div>
										</div>
									</div>
									<div className="skeleton-button"></div>
								</div>
							))
						) : webinars.length > 0 ? (
							webinars.map((webinar) => (
								<WebinarCard
									key={webinar._id}
									webinar={webinar}
									isAdmin={isAdmin}
									username={communityData.adminId.userName}
									onDelete={(webinarId) => handleDeleteWebinar(webinarId)}
									onUpdate={handleWebinarUpdate}
									communityId={communityData._id}
								/>
							))
						) : (
							<div className="no-items-message">No webinars available for this community</div>
						)}
					</div>
				)}

				{showUrlsPopup && (
					<div className="urls-popup">
						<div className="urls-popup-content">
							<h3>Resource URLs</h3>
							<div className="urls-list">
								{showUrlsPopup.URLS.map((url, index) => (
									<a
										key={index}
										href={url}
										target="_blank"
										rel="noopener noreferrer"
									>
										<span>Resource {index + 1}</span>
										<span className="urls-popup-open-btn">Open</span>
									</a>
								))}
							</div>
							<button
								className="urls-popup-close-btn"
								onClick={() => setShowUrlsPopup(null)}
							>
								Close
							</button>
						</div>
					</div>
				)}

				{showAddProduct && (
					<AddProductPopup
						communityId={communityData._id}
						onClose={() => setShowAddProduct(false)}
						onProductAdded={handleProductAdded}
						onProductUpdate={handleUpdateProduct}
						product={updateProduct}
					/>
				)}

				{showPaymentModal && selectedProduct && (
					<PurchasePopup
						isOpen={showPaymentModal}
						onClose={() => setShowPaymentModal(false)}
						itemDetails={{
							type: "product",
							name: selectedProduct.name,
							description: (selectedProduct.description),
							resourceCount: selectedProduct.URLS.length,
							amount: selectedProduct.amount,
							is_free: selectedProduct.is_free,
						}}
						loggedInUser={loggedInUser}
						onProceed={handlePaymentSuccess}
					/>
				)}

				{showAddService && (
					<AddServicePopup
						communityId={communityData._id}
						onClose={() => {
							setShowAddService(false);
							setEditingService(null);
						}}
						onServiceAdded={handleServiceAdded}
						editingService={editingService}
					/>
				)}

				{/* Add the Update Share Banner */}
				{showUpdateBanner && (
					<UpdateShareBanner
						component={updateType}
						onClose={handleUpdateBannerClose}
						setPopupOpen={(open) => {
							if (open) {
								handlePreparePost();
							} else {
								setPopupOpen(false);
							}
						}}
						entityId={communityData._id}
						description={generatePostContent()}
					/>
				)}

				{/* Add the Create Post Popup */}
				{popupOpen && (
					<CreatePostPopUp
						setPopupOpen={setPopupOpen}
						popupOpen={popupOpen}
						communityId={communityData._id}
					/>
				)}
			</div>
		</div>
	);
};

export default Products;
